import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCourseContext } from '../../CourseContext';
import { courseRunsService, CourseDocument } from '../../../../services/courseRunsService';
import { courseCandidatesService } from '../../../../services/courseCandidatesService';
import { courseSupportingDataService } from '../../../../services/courseSupportingDataService';
import { skillFrameworkService, SkillFramework, SkillFocus } from '../../../../services/skillFrameworkService';
import type { CefrRegistry } from '../../sections/SharedResources/CefrLevelForm';

// Helper to extract name from a legacy skill/category value that could be string or {id, name}
function skillName(val: string | { id: string; name: string } | undefined): string {
  if (!val) return '';
  return typeof val === 'string' ? val : val.name;
}

// Derive the initial skill_focus from targeting: prefer the structured field,
// fall back to the legacy skill_category + skills so older courses still load.
function initialSkillFocus(targeting: CourseDocument['targeting']): SkillFocus[] {
  if (Array.isArray(targeting.skill_focus) && targeting.skill_focus.length > 0) {
    return targeting.skill_focus.map((f) => ({
      category: f.category || '',
      skill: f.skill || '',
      custom: !!f.custom,
    }));
  }
  const category = skillName(targeting.skill_category);
  return (targeting.skills || []).map((s) => ({
    category,
    skill: skillName(s),
    custom: true, // legacy free-text; validated/normalized server-side
  }));
}

export function CourseSetup() {
  const { document, run, candidate, refreshDocument } = useCourseContext();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cefrLevelOptions, setCefrLevelOptions] = useState<string[]>([]);

  const targeting = document?.targeting;

  // Load CEFR levels from registry
  useEffect(() => {
    (async () => {
      try {
        const data = await courseSupportingDataService.get('cefr_levels');
        const registry = JSON.parse(data.content) as CefrRegistry;
        const singleLevels = Object.keys(registry.levels);
        const combined: string[] = [];
        for (let i = 0; i < singleLevels.length - 1; i++) {
          combined.push(`${singleLevels[i]}-${singleLevels[i + 1]}`);
        }
        setCefrLevelOptions([...singleLevels, ...combined]);
      } catch {
        setCefrLevelOptions(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'A1-A2', 'A2-B1', 'B1-B2', 'B2-C1', 'C1-C2']);
      }
    })();
  }, []);

  const handleSave = useCallback(async (formData: CourseDocument['targeting']) => {
    if (!run?.run_id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await courseRunsService.updateDocument(run.run_id, { targeting: formData });
      if (candidate?.course_id) {
        await courseCandidatesService.update(candidate.course_id, {
          title: formData.title,
          description: formData.description,
          audience: formData.audience,
          cefr_level: formData.cefr_level,
          skill_focus: formData.skill_focus,
          skills: formData.skills,
          skill_category: formData.skill_category,
          industry_focus: formData.industry_focus,
          suggested_lesson_count: formData.suggested_lesson_count,
          approx_lesson_minutes: formData.approx_lesson_minutes,
          test_policy: formData.test_policy as 'none' | 'end_of_course' | 'end_of_lesson' | 'both',
          additional_guidance: formData.additional_guidance,
        });
      }
      await refreshDocument();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to save';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [run?.run_id, candidate?.course_id, refreshDocument]);

  if (!targeting) {
    return <div className="p-6 text-gray-400 text-sm">Loading targeting data...</div>;
  }

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Course Setup</h2>
      <p className="text-sm text-gray-500 mb-6">
        Define the targeting parameters for your course. These will be used to generate the syllabus.
      </p>

      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{saveError}</div>
      )}

      <CourseSetupForm
        targeting={targeting}
        onSave={handleSave}
        saving={saving}
        cefrLevelOptions={cefrLevelOptions}
      />
    </div>
  );
}

function CourseSetupForm({
  targeting,
  onSave,
  saving,
  cefrLevelOptions,
}: {
  targeting: NonNullable<ReturnType<typeof useCourseContext>['document']>['targeting'];
  onSave: (data: CourseDocument['targeting']) => Promise<void>;
  saving: boolean;
  cefrLevelOptions: string[];
}) {
  const [title, setTitle] = useState(targeting.title);
  const [description, setDescription] = useState(targeting.description);
  const [additionalGuidance, setAdditionalGuidance] = useState(targeting.additional_guidance);
  const [audience, setAudience] = useState(targeting.audience);
  const [cefrLevel, setCefrLevel] = useState(targeting.cefr_level);
  const [industryFocus, setIndustryFocus] = useState(targeting.industry_focus);
  const [suggestedLessonCount, setSuggestedLessonCount] = useState(targeting.suggested_lesson_count);
  const [approxLessonMinutes, setApproxLessonMinutes] = useState(targeting.approx_lesson_minutes);
  const [testPolicy, setTestPolicy] = useState(targeting.test_policy);

  // Skill focus from the canonical framework taxonomy
  const [framework, setFramework] = useState<SkillFramework | null>(null);
  const [browseCategory, setBrowseCategory] = useState<string>('');
  const [skillFocus, setSkillFocus] = useState<SkillFocus[]>(initialSkillFocus(targeting));
  const [customSkill, setCustomSkill] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setFramework(await skillFrameworkService.get());
      } catch {
        // Silently fail — picker shows custom-add only
      }
    })();
  }, []);

  const browseSkills = useMemo(() => {
    if (!framework || !browseCategory) return [] as string[];
    const cat = framework.categories.find((c) => c.category === browseCategory);
    return cat ? cat.skills.map((s) => s.skill) : [];
  }, [framework, browseCategory]);

  const isSelected = (category: string, skill: string) =>
    skillFocus.some((f) => f.category === category && f.skill === skill);

  const toggleSkill = (category: string, skill: string) => {
    setSkillFocus((prev) =>
      prev.some((f) => f.category === category && f.skill === skill)
        ? prev.filter((f) => !(f.category === category && f.skill === skill))
        : [...prev, { category, skill, custom: false }],
    );
  };

  const addCustomSkill = () => {
    const skill = customSkill.trim();
    if (!skill) return;
    setSkillFocus((prev) =>
      prev.some((f) => f.skill === skill && f.category === browseCategory)
        ? prev
        : [...prev, { category: browseCategory, skill, custom: true }],
    );
    setCustomSkill('');
  };

  const removeFocus = (idx: number) =>
    setSkillFocus((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      title: title.trim(),
      description: description.trim(),
      audience: audience.trim(),
      cefr_level: cefrLevel,
      skill_focus: skillFocus,
      // Back-compat fields derived from skill_focus for any legacy reader.
      skills: skillFocus.map((f) => f.skill),
      skill_category: skillFocus[0]?.category || '',
      industry_focus: industryFocus.trim(),
      suggested_lesson_count: suggestedLessonCount,
      approx_lesson_minutes: approxLessonMinutes,
      test_policy: testPolicy,
      additional_guidance: additionalGuidance.trim(),
    });
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Title *</label>
        <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>

      <div>
        <label className={labelClass}>Additional Guidance</label>
        <textarea
          className={inputClass}
          rows={3}
          value={additionalGuidance}
          onChange={(e) => setAdditionalGuidance(e.target.value)}
          placeholder="Please provide detailed instructions to the model on what you think this course should look like. What do you want covered, what needs more or less coverage, what are some of the unique selling points of this course (we don't want generic courses). The model will fill in a lot of the details and do its own thinking but you have to give it directional guidance."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Audience *</label>
          <input className={inputClass} value={audience} onChange={(e) => setAudience(e.target.value)} required />
        </div>
        <div>
          <label className={labelClass}>CEFR Level *</label>
          <select className={inputClass} value={cefrLevel} onChange={(e) => setCefrLevel(e.target.value)}>
            {cefrLevelOptions.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Skill Focus — from the canonical skill framework */}
      <div>
        <label className={labelClass}>
          Skill Focus
          {skillFocus.length > 0 && (
            <span className="ml-2 font-normal text-gray-400">({skillFocus.length} selected)</span>
          )}
        </label>

        {/* Selected focus as removable pills */}
        {skillFocus.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {skillFocus.map((f, idx) => (
              <span
                key={`${f.category}|${f.skill}|${idx}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${
                  f.custom ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}
                title={f.category ? `${f.category} / ${f.skill}${f.custom ? ' (custom)' : ''}` : f.skill}
              >
                {f.category ? `${f.category} / ${f.skill}` : f.skill}{f.custom ? ' *' : ''}
                <button type="button" className="text-gray-400 hover:text-red-500" onClick={() => removeFocus(idx)}>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Category dropdown */}
        <select
          className={inputClass}
          value={browseCategory}
          onChange={(e) => setBrowseCategory(e.target.value)}
        >
          <option value="">Select a category to browse skills…</option>
          {(framework?.categories || []).map((c) => (
            <option key={c.category} value={c.category}>{c.category}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">
          Skills come from the Talaera skill framework. The lesson and exercise stages tag content with sub-skills under your chosen skills.
        </p>

        {/* Skills grid for the browsed category */}
        {browseCategory && (
          <div className="border border-gray-200 rounded-md p-2 mt-2 max-h-48 overflow-y-auto">
            {browseSkills.length === 0 ? (
              <div className="text-xs text-gray-400 py-2 text-center">No skills in this category</div>
            ) : (
              <div className="grid grid-cols-2 gap-1">
                {browseSkills.map((skill) => {
                  const sel = isSelected(browseCategory, skill);
                  return (
                    <button
                      key={skill}
                      type="button"
                      className={`text-left px-2.5 py-1.5 rounded text-xs transition-colors ${
                        sel ? 'bg-blue-100 text-blue-800 font-medium' : 'hover:bg-gray-50 text-gray-700'
                      }`}
                      onClick={() => toggleSkill(browseCategory, skill)}
                    >
                      {sel && <span className="mr-1">&#10003;</span>}
                      {skill}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Custom skill add */}
        <div className="flex gap-2 mt-2">
          <input
            className={inputClass}
            value={customSkill}
            onChange={(e) => setCustomSkill(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSkill(); } }}
            placeholder={browseCategory ? `Add a custom skill under ${browseCategory}…` : 'Add a custom skill (select a category first to file it)…'}
          />
          <button
            type="button"
            onClick={addCustomSkill}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 whitespace-nowrap"
          >
            Add custom
          </button>
        </div>
      </div>

      <div>
        <label className={labelClass}>Industry Focus</label>
        <input className={inputClass} value={industryFocus} onChange={(e) => setIndustryFocus(e.target.value)} placeholder="Technology, Finance..." />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Suggested Lesson Count</label>
          <input className={inputClass} type="number" min={1} max={20} value={suggestedLessonCount} onChange={(e) => setSuggestedLessonCount(Number(e.target.value))} />
        </div>
        <div>
          <label className={labelClass} title="For an individual at the target level for this course, how long will it take them to read the lesson and do the exercises">
            Approx. Lesson Length (minutes)
            <span className="ml-1 text-gray-400 cursor-help" title="For an individual at the target level for this course, how long will it take them to read the lesson and do the exercises">&#9432;</span>
          </label>
          <input className={inputClass} type="number" min={3} max={30} value={approxLessonMinutes} onChange={(e) => setApproxLessonMinutes(Number(e.target.value))} />
        </div>
      </div>

      <div>
        <label className={labelClass}>Test Policy</label>
        <select className={inputClass} value={testPolicy} onChange={(e) => setTestPolicy(e.target.value)}>
          <option value="none">None</option>
          <option value="end_of_course">End of Course</option>
          <option value="end_of_lesson">End of Lesson</option>
          <option value="both">Both</option>
        </select>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Setup'}
        </button>
      </div>
    </form>
  );
}
